import { useState, useEffect, useRef } from 'react'
import commonService from 'src/components/chatbot/services/commonService'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'

const TRIGGER_TYPES = ['sample:', 'disease:', 'hpo:', 'panel:']

const useAutoComplete = (getSuggestions, initialValue = '') => {
  const inputRef = useRef(null)
  const [triggerPosition, setTriggerPosition] = useState(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [suggestions, setSuggestions] = useState([])
  const { inputValue, setInputValue, getCachedData, updateCache } = useChatbotStore()
  const triggers = TRIGGER_TYPES
  const [isLoading, setIsLoading] = useState(false)

  const matchesAllWords = (name, query) => {
    const haystack = name.toLowerCase()
    return query
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .every((word) => haystack.includes(word))
  }

  const fetchSuggestions = async (trigger, query = '') => {
    try {
      setIsLoading(true)
      const contextFunctions = {
        getCachedData,
        updateCache,
      }

      switch (trigger) {
        case 'sample:':
          const samples = await commonService.fetchAllSamples(contextFunctions)
          return samples.filter((sample) => matchesAllWords(sample.name, query))
        case 'disease:':
          if (query) {
            const diseases = await commonService.fetchDiseases({ search: query })
            return diseases
          }
          // No empty-query fetch for diseases (on-demand only)
          return []
        case 'hpo:':
          const hpoTerms = await commonService.fetchHPOTerms(contextFunctions)
          return hpoTerms.filter((term) => matchesAllWords(term.name, query))
        case 'panel:':
          const panels = await commonService.fetchPanels(contextFunctions)
          return panels.filter((panel) => matchesAllWords(panel.name, query))
        default:
          return []
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      return []
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const handleInputChange = async () => {
      const regex = new RegExp(`(${triggers.join('|')})\\s*([^"]*)$`, 'i')
      const match = inputValue.match(regex)

      if (match) {
        const trigger = match[1].toLowerCase()
        const query = match[2].trim()

        setTriggerPosition({
          start: match.index,
          trigger: trigger,
        })

        const suggestions = await fetchSuggestions(trigger, query)
        setSuggestions(suggestions)
        setSelectedIndex(0)
      } else {
        setTriggerPosition(null)
      }
    }
    handleInputChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  const handleKeyDown = (e) => {
    if (triggerPosition) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % suggestions.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length)
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSelection(suggestions[selectedIndex])
      }
    }
  }

  const handleSelection = (selected) => {
    if (!triggerPosition || !selected) return

    // const cleanType = triggerPosition.trigger.replace(':', '')
    // addMapping(cleanType, selected.name, selected.id)

    const displayName = `"${selected.name}"`
    const newValue = `${inputValue.slice(0, triggerPosition.start)}${triggerPosition.trigger}${displayName} `

    setInputValue(newValue)
    setTriggerPosition(null)
    inputRef.current?.focus()

    setTimeout(() => {
      if (inputRef.current) {
        const inputElement = inputRef.current.querySelector('input') || inputRef.current

        inputElement.scrollLeft = inputElement.scrollWidth
      }
    }, 10)
  }

  return {
    inputRef,
    triggerPosition,
    suggestions,
    selectedIndex,
    handleKeyDown,
    handleSelection,
    inputValue,
    setInputValue,
    closeMenu: () => setTriggerPosition(null),
    isLoading,
  }
}

export default useAutoComplete
